import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import {
  type ChangeEvent,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import FilterSearchBar from "@/components/ui/molecules/FilterSearchBar";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import type { FilterDataWithLabels } from "@/store/news-feed/filters/newsFeed-selectors";
import FilterComponentItem from "./FilterComponentItem";

interface NewsFeedFilterSectionProps {
  title: string;
  category: ShareVoiceSidebarCategory;
  filterData: FilterDataWithLabels;
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
  onToggleFilter: (category: ShareVoiceSidebarCategory, item: string) => void;
  /** Number of items to show initially before showing search (default: 10) */
  visibleCount?: number;
  /** Whether the accordion is expanded */
  isExpanded?: boolean;
  /** Callback when accordion expansion changes */
  onValueChange?: (value: string) => void;
  /** Control text truncation vs wrapping for filter item labels */
  labelTextWrap?: "truncate" | "wrap";
}

/**
 * Memoized filter section component for news feed filters
 * Handles loading, error states, search filtering, and renders filter items
 */
const NewsFeedFilterSection = ({
  title,
  category,
  filterData,
  selectedFilters,
  onToggleFilter,
  visibleCount: initialVisibleCount = 10,
  isExpanded = false,
  onValueChange,
  labelTextWrap,
}: NewsFeedFilterSectionProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [showError, setShowError] = useState(false);
  const titleRef = useRef(title);

  // Keep title ref up to date without triggering effect re-runs
  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  // Reset visible count when accordion is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setVisibleCount(initialVisibleCount);
    }
  }, [isExpanded, initialVisibleCount]);

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return filterData.items;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return filterData.items.filter((item) =>
      item.label.toLowerCase().includes(lowerSearchTerm),
    );
  }, [filterData.items, searchTerm]);

  const visibleItems = useMemo(() => {
    return filteredItems.slice(0, visibleCount);
  }, [filteredItems, visibleCount]);

  const hasMoreItems = visibleCount < filteredItems.length;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
    setVisibleCount(initialVisibleCount); // Reset to initial count when searching
  };

  const handleClearSearch = (): void => {
    setSearchTerm("");
    setVisibleCount(initialVisibleCount); // Reset to initial count when clearing search
  };

  const handleShowMore = (): void => {
    setVisibleCount((prev) =>
      Math.min(prev + initialVisibleCount, filteredItems.length),
    );
  };

  const handleCollapse = (): void => {
    setVisibleCount(initialVisibleCount);
  };

  const showSearch = filterData.items.length > initialVisibleCount;

  const showSkeleton = filterData.isLoading && filterData.items.length === 0;

  // Button classes for both show more and show less buttons
  const buttonClasses =
    "text-[10px] font-medium text-gray-500 flex items-center gap-1 px-0 h-8 hover:text-gray-700 underline-offset-4 underline";

  useEffect(() => {
    if (!filterData.error) {
      setShowError(false);
      return;
    }

    console.error(`${titleRef.current} error:`, filterData.error);
    setShowError(true);

    const timer = setTimeout(() => {
      setShowError(false);
    }, 15000);

    return () => {
      clearTimeout(timer);
    };
  }, [filterData.error]);

  const renderContent = () => {
    if (showSkeleton) {
      return <ItemSkeleton showNestedItems={false} />;
    }

    return (
      <>
        {showSearch && (
          <FilterSearchBar
            searchTerm={searchTerm}
            title={title}
            onSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
            isOpen={isExpanded}
          />
        )}

        {filterData.error && showError && (
          <div className="text-xxs text-red-500 leading-snug py-0.5">
            An error occurred while fetching the filters. Please try again
            later.
          </div>
        )}

        {filteredItems.length === 0 && searchTerm.trim() && (
          <div className="text-xs text-gray-500 py-2">
            No filters found matching "{searchTerm}"
          </div>
        )}

        {visibleItems.map((item, index) => (
          <FilterComponentItem
            key={item.categoryLabel}
            title={item.label}
            count={item.total}
            isCountLoading={filterData.isLoading}
            selected={
              selectedFilters.get(category)?.includes(item.categoryLabel) ??
              false
            }
            tabIndex={index}
            toggleFilter={() => onToggleFilter(category, item.categoryLabel)}
            className="px-0"
            labelTextWrap={labelTextWrap}
          />
        ))}

        {hasMoreItems && (
          <Button
            variant="none"
            size="sm"
            onPress={handleShowMore}
            className={buttonClasses}
          >
            <CaretDownIcon size={12} />
            Show More ({filteredItems.length - visibleCount} remaining)
          </Button>
        )}

        {!hasMoreItems && visibleCount > initialVisibleCount && (
          <Button
            variant="none"
            size="sm"
            onPress={handleCollapse}
            className={buttonClasses}
          >
            <CaretUpIcon size={12} />
            Show Less
          </Button>
        )}
      </>
    );
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? category : ""}
      onValueChange={onValueChange}
    >
      <AccordionItem value={category} className="px-2">
        <AccordionTrigger className="font-medium text-xs text-gray-700 py-2">
          {title}
        </AccordionTrigger>
        <AccordionContent className="pb-2">{renderContent()}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

NewsFeedFilterSection.displayName = "NewsFeedFilterSection";

export default memo(NewsFeedFilterSection);
